import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Button, Badge, ListGroup } from 'react-bootstrap';
import {
  ModalAddOPIOperatorParticipant,
  ModalOutboundDtmfDial,
} from '../../../features';
import { CallType, CallStatusType } from '../../../configs';
import { ActionAudioCall, useAppDispatch, useAppSelector, VRIAppStateType } from '../../../core';
import { AudioConferenceCall, AudioConferenceCalls } from '../../../models';
import { SendOutboundDtmfRequestModel, ThreeWayCallReDialRequest } from '../../../models/api/Call';
import { formatSupportedPhoneNumber, parseConferenceParticipantNumber } from '../../../utils/phoneNumberFormatting';

type Props = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (value: boolean) => void;
  setShowOutboundModal: (value: boolean) => void;
  handleHangup: Function;
  callinfo: any;
  onCallConferenceParticipant: (value: ThreeWayCallReDialRequest) => Promise<any>;
  audioConferenceCallsOverride?: AudioConferenceCalls;
  uiOnly?: boolean;
  onUiOnlyInitiated?: (phoneNumber: string, displayName?: string) => void;
  onUiOnlyHangup?: (phoneNumber: string) => void;
  onSendOutboundDtmf?: (value: SendOutboundDtmfRequestModel) => Promise<any>;
};

export function OpiConferenceSidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  setShowOutboundModal,
  handleHangup,
  callinfo,
  onCallConferenceParticipant,
  audioConferenceCallsOverride,
  uiOnly = false,
  onUiOnlyInitiated,
  onUiOnlyHangup,
  onSendOutboundDtmf,
}: Props) {
  const getRedialPayload = (fullPhoneNumber: string): ThreeWayCallReDialRequest => {
    const parsedPhoneNumber = parseConferenceParticipantNumber(fullPhoneNumber);

    return {
      phoneNumber: parsedPhoneNumber.phoneNumber,
      countryCode: parsedPhoneNumber.countryCode,
      callSid: callinfo.customParameters?.get("CallSid"),
    };
  };

  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showCompletedCalls, setShowCompletedCalls] = useState(false);
  const [completedHeight, setCompletedHeight] = useState(200);
  const [showKeypadModal, setShowKeypadModal] = useState(false);
  const [selectedKeypadCalledNumber, setSelectedKeypadCalledNumber] = useState<string | null>(null);
  const isResizingRef = useRef(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const completedContainerRef = useRef<HTMLDivElement | null>(null);
  const [pendingHangups, setPendingHangups] = useState<Set<string>>(new Set());
  const prevCompletedCountRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [recentlyAnsweredIds, setRecentlyAnsweredIds] = useState<Set<string>>(new Set());
  const lastStatusRef = useRef<Map<string, CallStatusType | undefined>>(new Map());
  const [ringingDots, setRingingDots] = useState(1);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const prevPositionsRef = useRef<Map<string, number>>(new Map());
  const animatingRef = useRef(false);
  const queuedRef = useRef(false);
  const [animationTick, setAnimationTick] = useState(0);
  const prevKeysRef = useRef<Set<string>>(new Set());
  const [renderCalls, setRenderCalls] = useState<AudioConferenceCalls>([]);
  const removingRef = useRef<Set<string>>(new Set());

  const dispatch = useAppDispatch();
  const audioConferenceCalls = useAppSelector((state: VRIAppStateType) => state.audioCall.audioConferenceCalls);
  const displayCalls = audioConferenceCallsOverride ?? audioConferenceCalls;
  const selectedKeypadCall =
    selectedKeypadCalledNumber
      ? (displayCalls || []).find((call) => call.calledNumber === selectedKeypadCalledNumber) ?? null
      : null;

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      const rect = sidebarRef.current?.getBoundingClientRect();
      const bottom = rect ? rect.bottom - 48 : window.innerHeight - 48;
      const newHeight = Math.min(300, Math.max(32, bottom - event.clientY));
      setCompletedHeight(newHeight);
      if (newHeight > 32 && !showCompletedCalls) {
        setShowCompletedCalls(true);
      }
    };
    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showCompletedCalls]);

  useEffect(() => {
    const completedStatuses = new Set<CallStatusType>([
      CallStatusType.OutboundCallCompleted,
      CallStatusType.OutboundCallNotAnswered,
      CallStatusType.Failed,
    ]);
    const allCalls = displayCalls || [];
    const completedIds = allCalls
      .filter((c) => completedStatuses.has(c.status?.current as CallStatusType))
      .map((c) => c.callSid || c.calledNumber);

    if (prevCompletedCountRef.current === 0 && completedIds.length > 0) {
      setShowCompletedCalls(true);
    }
    prevCompletedCountRef.current = completedIds.length;
  }, [displayCalls]);

  useEffect(() => {
    if (!displayCalls || displayCalls.length === 0) return;
    const newAnsweredIds: string[] = [];
    const nextStatusMap = new Map<string, CallStatusType | undefined>();

    displayCalls.forEach((call) => {
      const key = call.callSid || call.calledNumber;
      if (!key) return;
      const currentStatus = call.status?.current;
      const prevStatus = lastStatusRef.current.get(key);
      nextStatusMap.set(key, currentStatus);

      if (
        currentStatus === CallStatusType.OutboundCallReceived &&
        prevStatus !== CallStatusType.OutboundCallReceived
      ) {
        newAnsweredIds.push(key);
      }
    });

    if (newAnsweredIds.length > 0) {
      setRecentlyAnsweredIds((prev) => {
        const next = new Set(prev);
        newAnsweredIds.forEach((id) => next.add(id));
        return next;
      });
      newAnsweredIds.forEach((id) => {
        setTimeout(() => {
          setRecentlyAnsweredIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, 3000);
      });
    }

    lastStatusRef.current = nextStatusMap;
  }, [displayCalls]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRingingDots((prev) => (prev % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    setRenderCalls((prev) => {
      const nextMap = new Map<string, AudioConferenceCall>();
      const prevMap = new Map<string, AudioConferenceCall>();
      prev.forEach((c) => prevMap.set(c.callSid || c.calledNumber, c));
      (displayCalls || []).forEach((c) => {
        const key = c.callSid || c.calledNumber;
        if (!key) return;
        nextMap.set(key, c);
      });

      nextMap.forEach((c, key) => {
        prevMap.set(key, c);
      });

      prevMap.forEach((c, key) => {
        if (!nextMap.has(key)) {
          removingRef.current.add(key);
        }
      });

      return Array.from(prevMap.values());
    });
  }, [displayCalls]);

  useLayoutEffect(() => {
    if (animatingRef.current) {
      queuedRef.current = true;
      return;
    }

    const newPositions = new Map<string, number>();
    itemRefs.current.forEach((el, key) => {
      newPositions.set(key, el.getBoundingClientRect().top);
    });

    let didAnimate = false;
    prevPositionsRef.current.forEach((prevTop, key) => {
      const newTop = newPositions.get(key);
      const el = itemRefs.current.get(key);
      if (el && newTop !== undefined) {
        const delta = prevTop - newTop;
        if (Math.abs(delta) > 1) {
          el.dataset.animating = '1';
          el.style.transition = 'none';
          el.style.transform = `translateY(${delta}px)`;
          requestAnimationFrame(() => {
            el.style.transition = 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1)';
            el.style.transform = '';
          });
          didAnimate = true;
        }
      }
    });

    newPositions.forEach((_, key) => {
      if (!prevPositionsRef.current.has(key)) {
        const el = itemRefs.current.get(key);
        if (!el) return;
        el.style.opacity = '0';
        el.style.transform = 'translateY(-6px)';
        el.style.transition = 'none';
        requestAnimationFrame(() => {
          el.style.transition = 'opacity 1500ms ease, transform 1500ms ease';
          el.style.opacity = '1';
          el.style.transform = '';
        });
      }
    });

    prevPositionsRef.current = newPositions;
    prevKeysRef.current = new Set(newPositions.keys());

    if (didAnimate) {
      animatingRef.current = true;
      setTimeout(() => {
        animatingRef.current = false;
        itemRefs.current.forEach((el) => {
          if (el.dataset.animating === '1') {
            el.dataset.animating = '0';
            el.style.transition = '';
          }
        });
        if (queuedRef.current) {
          queuedRef.current = false;
          setAnimationTick((tick) => tick + 1);
        }
      }, 700);
    }
  }, [renderCalls, animationTick]);

  useEffect(() => {
    if (!displayCalls || displayCalls.length === 0) return;
    setPendingHangups((prev) => {
      const next = new Set(prev);
      displayCalls.forEach((call) => {
        const key = call.callSid || call.calledNumber;
        if (!key) return;
        if (
          call.status?.current === CallStatusType.OutboundCallCompleted ||
          call.status?.current === CallStatusType.OutboundCallNotAnswered ||
          call.status?.current === CallStatusType.Failed
        ) {
          next.delete(key);
        }
      });
      return next;
    });
  }, [displayCalls]);

  useEffect(() => {
    if (!showCompletedCalls || !completedContainerRef.current) return;
    return () => {
      observerRef.current?.disconnect();
    };
  }, [showCompletedCalls, displayCalls]);

  const getCallStatusLabel = (current?: CallStatusType, prev?: CallStatusType): string => {
    if (!current) return "waiting...";

    switch (current) {
      case CallStatusType.OutboundCallInitiated:
        return "Initiated";
      case CallStatusType.OutboundCallRinging:
        return "Ringing";
      case CallStatusType.OutboundCallReceived:
        return "In-call";
      case CallStatusType.OutboundCallNotAnswered:
        return "Not Answered";
      case CallStatusType.OutboundCallNotReceived:
        return "Voice Mail";
      case CallStatusType.OutboundCallCompleted:
        if (prev === CallStatusType.OutboundCallReceived) return "Hung Up";
        if (prev === CallStatusType.OutboundCallNotAnswered) return "Not Answered";
        return "Completed";
      case CallStatusType.Failed:
        return "Failed";
      default:
        return "waiting...";
    }
  };

  const callAgain = (phoneNumber: string, displayName?: string) => {
    const value = getRedialPayload(phoneNumber);

    if (uiOnly) {
      onUiOnlyInitiated?.(phoneNumber, displayName);
    } else {
      dispatch(ActionAudioCall.UpsertAudioConferenceCall({
        calledNumber: phoneNumber,
        displayName,
        status: {
          current: CallStatusType.OutboundCallInitiated,
        }
      }));
    }

    onCallConferenceParticipant(value)
      .then((response) => {
        if (response || uiOnly) return;

        dispatch(ActionAudioCall.UpdateAudioConferenceCallStatus({
          calledNumber: phoneNumber,
          current: CallStatusType.Failed,
          callSid: "",
        }));
      })
      .catch(() => {
        if (!uiOnly) {
          dispatch(ActionAudioCall.UpdateAudioConferenceCallStatus({
            calledNumber: phoneNumber,
            current: CallStatusType.Failed,
            callSid: "",
          }));
        }
      });
  };

  const renderHangupButton = (onClick: () => void, isPending: boolean) => (
    <Button
      variant="danger"
      size="sm"
      onClick={onClick}
      disabled={isPending}
      style={{ padding: '2px 6px', lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: '7px' }}
    >
      {isPending ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="16"
          height="16"
          fill="white"
          style={{ transform: 'rotate(135deg)' }}
        >
          <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
        </svg>
      )}
      <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2px' }}>Hangup</span>
    </Button>
  );

  const renderRedialButton = (onClick: () => void) => (
    <Button
      variant="success"
      title="call again"
      size="sm"
      onClick={onClick}
      style={{ padding: '2px 6px', lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: '7px' }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        fill="white"
      >
        <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z" />
      </svg>
      <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.2px' }}>Redial</span>
    </Button>
  );

  const renderKeypadButton = (callee: AudioConferenceCall) => (
    <Button
      variant="secondary"
      size="sm"
      title="Open keypad"
      onClick={() => {
        setSelectedKeypadCalledNumber(callee.calledNumber);
        setShowKeypadModal(true);
      }}
      style={{ padding: '2px 4px', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}
      disabled={!onSendOutboundDtmf}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="white"
      >
        <path d="M7 2H5a2 2 0 0 0-2 2v2h4V2zm7 0H10v4h4V2zm5 0h-2v4h4V4a2 2 0 0 0-2-2zM7 9H3v4h4V9zm7 0H10v4h4V9zm7 0h-4v4h4V9zM7 16H3v4a2 2 0 0 0 2 2h2v-6zm7 0H10v6h4v-6zm3 0v6h2a2 2 0 0 0 2-2v-4h-4z" />
      </svg>
    </Button>
  );

  return (
    <>
      <div
        ref={sidebarRef}
        style={{
          position: 'fixed',
          top: 0,
          right: isSidebarOpen ? 0 : '-300px',
          width: '300px',
          height: '100%',
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          boxShadow: '-2px 0 5px rgba(0,0,0,0.5)',
          padding: '1rem',
          transition: 'right 0.3s ease',
          zIndex: 1000,
        }}
      >
        <style>{`
          @keyframes opiMicPulse {
            0% { box-shadow: 0 0 0 0 rgba(243, 211, 122, 0.6); }
            70% { box-shadow: 0 0 0 8px rgba(243, 211, 122, 0); }
            100% { box-shadow: 0 0 0 0 rgba(243, 211, 122, 0); }
          }
          @keyframes opiRingingPulse {
            0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.45); }
            60% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0); }
            100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
          }
        `}</style>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            position: 'absolute',
            left: '-40px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '40px',
            height: '80px',
            borderRadius: '0.5rem 0 0 0.5rem',
            backgroundColor: '#333',
            color: '#fff',
            border: '1px solid #555',
            cursor: 'pointer',
            boxShadow: '-2px 0 5px rgba(0,0,0,0.5)',
            writingMode: 'vertical-rl',
            textOrientation: 'upright',
          }}
        >
          {isSidebarOpen ? '→' : '←'}
        </button>

        <div className="d-flex flex-column justify-content-between" style={{ height: "100%", position: 'relative' }}>
          <div className="callees-section" style={{ flex: 1, overflowY: 'auto', paddingBottom: '60px' }}>
            <h4 style={{ marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
              Active Calls
            </h4>
            <div style={{ maxHeight: 'calc(100% - 120px)', overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {(() => {
                  const completedStatuses = new Set<CallStatusType>([
                    CallStatusType.OutboundCallCompleted,
                    CallStatusType.OutboundCallNotAnswered,
                    CallStatusType.Failed,
                  ]);

                  const allCalls = [...(renderCalls || [])];
                  allCalls.sort((a, b) => {
                    const aTime = a.lastUpdatedAt ? Date.parse(a.lastUpdatedAt) : 0;
                    const bTime = b.lastUpdatedAt ? Date.parse(b.lastUpdatedAt) : 0;
                    return bTime - aTime;
                  });

                  const activeCalls = allCalls.filter(
                    (callee) => !completedStatuses.has(callee.status?.current as CallStatusType)
                  );

                  return (
                    <>
                      {activeCalls.map((callee) => (
                        <ListGroup.Item
                          key={callee.callSid || callee.calledNumber}
                          ref={(el: HTMLAnchorElement | null) => {
                            const key = callee.callSid || callee.calledNumber;
                            if (!key) return;
                            if (el) {
                              itemRefs.current.set(key, el);
                              if (removingRef.current.has(key)) {
                                el.style.opacity = '0';
                                el.style.transition = 'opacity 1500ms ease';
                                setTimeout(() => {
                                  removingRef.current.delete(key);
                                  setRenderCalls((prev) =>
                                    prev.filter((c) => (c.callSid || c.calledNumber) !== key)
                                  );
                                }, 1500);
                              }
                            } else {
                              itemRefs.current.delete(key);
                            }
                          }}
                          className="d-flex justify-content-between align-items-center text-light mb-1"
                          style={{
                            background:
                              callee.status.current === CallStatusType.OutboundCallNotReceived
                                ? 'linear-gradient(90deg, rgba(66,56,24,0.9), rgba(43,47,54,0.95))'
                                : callee.status.current === CallStatusType.OutboundCallReceived
                                  ? 'linear-gradient(90deg, rgba(22,54,34,0.92), rgba(43,47,54,0.95))'
                                  : '#2b2f36',
                            borderRadius: '10px',
                            padding: '6px 10px',
                            border:
                              callee.status.current === CallStatusType.OutboundCallNotReceived
                                ? '1px solid #5c4b1b'
                                : callee.status.current === CallStatusType.OutboundCallReceived
                                  ? '1px solid #2f5a42'
                                  : '1px solid #3a4049',
                            minHeight: '44px',
                          }}
                        >
                          <div className="d-flex flex-column align-items-start" style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.2, opacity: 0.65 }}>
                              {callee.callOrder
                                ? `#${callee.callOrder} ${callee.displayName ? callee.displayName : `callee ${callee.callOrder}`}`
                                : callee.displayName || 'callee'}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.2 }}>
                              {formatSupportedPhoneNumber(callee.calledNumber)}
                            </div>
                            {callee.status.current === CallStatusType.OutboundCallNotReceived && (
                              <div
                                style={{
                                  marginTop: '4px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '11px',
                                  color: '#f3d37a',
                                }}
                              >
                                <span
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '999px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 0 0 0 rgba(243, 211, 122, 0.7)',
                                    animation: 'opiMicPulse 1.6s ease-out infinite',
                                    background: 'rgba(243, 211, 122, 0.15)',
                                    border: '1px solid rgba(243, 211, 122, 0.45)',
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    width="12"
                                    height="12"
                                    fill="currentColor"
                                  >
                                    <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V20H9v2h6v-2h-2v-2.08A7 7 0 0 0 19 11z" />
                                  </svg>
                                </span>
                                <span style={{ marginBottom: '0px' }}>Voice mail recording</span>
                              </div>
                            )}
                            <Badge
                              className={
                                callee.status.current === CallStatusType.OutboundCallCompleted
                                  ? 'custom-purple-badge'
                                  : ''
                              }
                              variant={
                                callee.status.current === CallStatusType.OutboundCallInitiated ||
                                  callee.status.current === CallStatusType.OutboundCallRinging
                                  ? 'primary'
                                  : callee.status.current === CallStatusType.OutboundCallReceived
                                    ? 'success'
                                    : callee.status.current === CallStatusType.OutboundCallNotAnswered ||
                                      callee.status.current === CallStatusType.OutboundCallNotReceived
                                      ? 'warning'
                                      : callee.status.current === CallStatusType.Failed
                                        ? 'danger'
                                      : 'secondary'
                              }
                              style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                marginTop: '2px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '2px',
                                animation:
                                  callee.status.current === CallStatusType.OutboundCallRinging
                                    ? 'opiRingingPulse 1.4s ease-in-out infinite'
                                    : undefined,
                              }}
                            >
                              {callee.status.current === CallStatusType.OutboundCallReceived &&
                                recentlyAnsweredIds.has(callee.callSid || callee.calledNumber) && (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ width: '10px', height: '10px', borderWidth: '2px' }}
                                  />
                                )}
                              <span>{getCallStatusLabel(callee.status.current, callee.status.prev)}</span>
                              {callee.status.current === CallStatusType.OutboundCallRinging && (
                                <span>{'.'.repeat(ringingDots)}</span>
                              )}
                            </Badge>
                          </div>

                          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            {(callee.status.current === CallStatusType.OutboundCallNotReceived ||
                              callee.status.current === CallStatusType.OutboundCallReceived ||
                              callee.status.current === CallStatusType.OutboundCallRinging) &&
                              renderHangupButton(() => {
                                const key = callee.callSid || callee.calledNumber;
                                if (key) {
                                  setPendingHangups((prev) => new Set(prev).add(key));
                                }
                                if (callee.callSid) {
                                  handleHangup(callee.callSid);
                                }
                              }, pendingHangups.has(callee.callSid || callee.calledNumber) || !callee.callSid)}
                            {renderKeypadButton(callee)}
                            {(callee.status.current === CallStatusType.OutboundCallCompleted ||
                              callee.status.current === CallStatusType.OutboundCallNotAnswered ||
                              callee.status.current === CallStatusType.Failed) &&
                              renderRedialButton(() => callAgain(callee.calledNumber, callee.displayName))}
                            {uiOnly &&
                              callee.status.current === CallStatusType.OutboundCallInitiated &&
                              null}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </>
                  );
                })()}
              </ListGroup>
            </div>
          </div>
          <div className="d-flex">
            <button
              style={{ backgroundColor: "var(--purple)", borderWidth: "0px", padding: '6px 10px', fontSize: '13px' }}
              className="btn btn-secondary mr-2 flex-grow-1"
              role="button"
              onClick={() => {
                setShowOutboundModal(true);
              }}
            >
              Call Participant
            </button>
            <button
              className="btn btn-secondary flex-grow-1"
              role="button"
              style={{ padding: '6px 10px', fontSize: '13px' }}
              onClick={() => {
                setShowAddParticipantModal(true);
              }}
            >
              Add Interpreter
            </button>
          </div>
          {(() => {
            const completedStatuses = new Set<CallStatusType>([
              CallStatusType.OutboundCallCompleted,
              CallStatusType.OutboundCallNotAnswered,
              CallStatusType.Failed,
            ]);
            const allCalls = displayCalls || [];
            const completedCalls = allCalls.filter(
              (callee) => completedStatuses.has(callee.status?.current as CallStatusType)
            );
            const completedCount = completedCalls.length;
            return (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: '48px',
                  background: '#1f232a',
                  borderTop: '1px solid #2c323b',
                  boxShadow: '0 -6px 12px rgba(0,0,0,0.25)',
                  borderRadius: '10px 10px 0 0',
                  height: showCompletedCalls ? `${completedHeight}px` : '32px',
                  transition: 'height 0.2s ease',
                  overflow: 'hidden',
                  zIndex: 2,
                }}
              >
                {showCompletedCalls && (
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      isResizingRef.current = true;
                      document.body.style.cursor = 'ns-resize';
                      document.body.style.userSelect = 'none';
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      cursor: 'ns-resize',
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setShowCompletedCalls((prev) => !prev)}
                  style={{
                    width: '100%',
                    height: '32px',
                    background: '#1a1e24',
                    color: '#d7dbe2',
                    border: '0',
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <span>Completed calls list</span>
                  <span style={{ opacity: 0.8 }}>
                    {completedCount} {showCompletedCalls ? '▾' : '▴'}
                  </span>
                </button>
                <div
                  ref={completedContainerRef}
                  style={{
                    maxHeight: `${Math.max(0, completedHeight - 32)}px`,
                    overflowY: 'auto',
                    padding: '8px 10px',
                    background: '#1f232a',
                  }}
                >
                  {completedCalls.length === 0 && (
                    <div style={{ fontSize: '12px', opacity: 0.7, padding: '6px 4px' }}>
                      no current entries
                    </div>
                  )}
                  {completedCalls.map((callee) => {
                    const id = callee.callSid || callee.calledNumber;
                    return (
                      <div
                        key={`${id}-completed`}
                        data-completed-id={id}
                        className="d-flex justify-content-between align-items-center text-light mb-1"
                        style={{
                          background: '#262b33',
                          borderRadius: '10px',
                          padding: '6px 10px',
                          border: '1px solid #333a45',
                          minHeight: '44px',
                        }}
                      >
                        <div className="d-flex flex-column align-items-start" style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.2, opacity: 0.65 }}>
                            {callee.displayName || 'callee'}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.2 }}>
                            {formatSupportedPhoneNumber(callee.calledNumber)}
                          </div>
                          <Badge
                            className={
                              callee.status.current === CallStatusType.OutboundCallCompleted
                                ? 'custom-purple-badge'
                                : ''
                            }
                            variant={
                              callee.status.current === CallStatusType.OutboundCallInitiated ||
                                callee.status.current === CallStatusType.OutboundCallRinging
                                ? 'primary'
                                : callee.status.current === CallStatusType.OutboundCallReceived
                                  ? 'success'
                                  : callee.status.current === CallStatusType.OutboundCallNotAnswered ||
                                    callee.status.current === CallStatusType.OutboundCallNotReceived
                                    ? 'warning'
                                    : callee.status.current === CallStatusType.Failed
                                      ? 'danger'
                                    : 'secondary'
                            }
                            style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              marginTop: '2px',
                            }}
                          >
                            {getCallStatusLabel(callee.status.current, callee.status.prev)}
                          </Badge>
                        </div>
                        {(callee.status.current === CallStatusType.OutboundCallCompleted ||
                          callee.status.current === CallStatusType.OutboundCallNotAnswered ||
                          callee.status.current === CallStatusType.Failed) &&
                          renderRedialButton(() => callAgain(callee.calledNumber, callee.displayName))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {onSendOutboundDtmf && (
        <ModalOutboundDtmfDial
          show={showKeypadModal}
          handleShow={setShowKeypadModal}
          callSid={callinfo?.customParameters?.get("CallSid") ?? null}
          selectedCall={selectedKeypadCall}
          onSendOutboundDtmf={onSendOutboundDtmf}
        />
      )}
      <ModalAddOPIOperatorParticipant
        callType={CallType.Audio}
        show={showAddParticipantModal}
        handleShow={setShowAddParticipantModal}
        callinfo={callinfo}
      />
    </>
  );
}
